import { Component, OnInit, ElementRef, ViewChild, Input, AfterViewInit } from '@angular/core';
import { FormGroup, FormControl, Validators, AbstractControl } from '@angular/forms';
import { IonContent, NavController } from '@ionic/angular';
import { BalanceModel } from 'src/app/models/balance-model';
import { CurrencyFormatterPipe } from 'src/app/pipes/currency-formatter/currency-formatter.pipe';
import { DateFormatPipe } from 'src/app/pipes/date-format/date-format.pipe';
import { AccountBalanceService } from 'src/app/services/balance/account-balance.service';
import { LimitsService } from 'src/app/services/limits/limits.service';
import { SuccessMessageService } from 'src/app/services/message-provider/success-message.service';
import { ResponsibleGamingService } from 'src/app/services/responsible-gaming-service/responsible-gaming.service';
import { UtilsService } from 'src/app/services/utils/utils.service';
import { SiteContentInterface } from 'src/app/interfaces/sitecontent-interface';
import { SiteContentModel } from 'src/app/models/sitecontent-model';
import { SiteContentService } from 'src/app/services/sitecontent/sitecontent.service';

@Component({
    selector: 'page-set-deposit-limits',
    templateUrl: './set-deposit-limits.component.html',
    styleUrls: ['./set-deposit-limits.component.scss'],
})
export class SetDepositLimitsComponent implements OnInit, AfterViewInit {
    @Input() previousPage: string = "";
    @ViewChild('scrollableContent', {}) scrollableContent: ElementRef;
    private scrollContainer: any;

    ngAfterViewInit() {
        if (this.scrollContainer) {
            this.scrollContainer = this.scrollableContent.nativeElement;
        }

        this.siteContentService.loadConfig('deposit_limit')
            .then((response: SiteContentInterface) => {
                this.labels = response
            })

    }

    @ViewChild(IonContent, { static: false }) content: IonContent;

    public error = null;

    public depositLimitsForm: FormGroup;

    public deposit_daily_limit: AbstractControl;
    public deposit_weekly_limit: AbstractControl;
    public deposit_monthly_limit: AbstractControl;

    public dailyLimitError: boolean = false;
    public weeklyLimitError: boolean = false;
    public monthlyLimitError: boolean = false;

    public dailyDepositLimits: any;
    public weeklyDepositLimits: any;
    public monthlyDepositLimits: any;

    public depositLimits: any[] = [];
    public labels: SiteContentInterface;
    public globalDailyLimit: any;
    public globalWeeklyLimit: any;
    public globalMonthlyLimit: any;
    public globalLimits: any[] = [];
    public currency: any;
    private floatingPointValidator = "^[+-]?([0-9]+([.][0-9]*)?|[.][0-9]+)$";

    constructor(
        private siteContentService: SiteContentService,
        private limitsService: LimitsService,
        private responsibleGamingProvider: ResponsibleGamingService,
        public successMessageService: SuccessMessageService,
        public navCtrl: NavController,
        public utils: UtilsService,
        public currencyFormatterPipe: CurrencyFormatterPipe,
        public accountBalanceService: AccountBalanceService,
        public dateFormatPipe: DateFormatPipe
    ) {
        this.labels = new SiteContentModel();
        this.successMessageService.clearMessage();
        this.error = null;
    }

    ngOnInit() {
        this.depositLimitsForm = new FormGroup(
            {
                deposit_daily_limit: new FormControl('', [Validators.pattern(this.floatingPointValidator)]),
                deposit_weekly_limit: new FormControl('', [Validators.pattern(this.floatingPointValidator)]),
                deposit_monthly_limit: new FormControl('', [Validators.pattern(this.floatingPointValidator)])
            }, this.atLeastOneFieldValidator
        );

        this.deposit_daily_limit = this.depositLimitsForm.get('deposit_daily_limit');
        this.deposit_weekly_limit = this.depositLimitsForm.get('deposit_weekly_limit');
        this.deposit_monthly_limit = this.depositLimitsForm.get('deposit_monthly_limit');

        this.getGlobalLimits();
        this.getDepositLimits();

        // we need to get currency
        this.accountBalanceService.getBalance().subscribe((response: BalanceModel) => {
            this.currency = response.getDefaultCurrency();
        });
    }

    getGlobalLimits() {
        this.limitsService.callGetGlobalPaymentLimits().subscribe({
            next: (response: any) => {
                if (response && !!response.result) {
                    const tempLimits = response.payload;

                    for (const key in tempLimits) {
                        if (Object.prototype.hasOwnProperty.call(tempLimits, key)) {
                            const obj = tempLimits[key];
                            for (const prop in obj) {
                                if (Object.prototype.hasOwnProperty.call(obj, prop)) {
                                    if (obj[prop].LMT_TRM_IN_DAYS == "DAILY") {
                                        this.globalDailyLimit = obj[prop].LMT_TRM_MAX_AMT;
                                        this.deposit_daily_limit.setValidators([Validators.pattern(this.floatingPointValidator), Validators.max(this.globalDailyLimit)]);
                                    }
                                    else if (obj[prop].LMT_TRM_IN_DAYS == "WEEKLY") {
                                        this.globalWeeklyLimit = obj[prop].LMT_TRM_MAX_AMT;
                                        this.deposit_weekly_limit.setValidators([Validators.pattern(this.floatingPointValidator), Validators.max(this.globalWeeklyLimit)]);
                                    }
                                    else if (obj[prop].LMT_TRM_IN_DAYS == "MONTHLY") {
                                        this.globalMonthlyLimit = obj[prop].LMT_TRM_MAX_AMT;
                                        this.deposit_monthly_limit.setValidators([Validators.pattern(this.floatingPointValidator), Validators.max(this.globalMonthlyLimit)]);
                                    }
                                }
                            }
                        }
                    }

                    this.updateDepositLimits(this.depositLimits);
                }
            },
            error: (error) => {
                this.error = error;
            }
        });
    }

    getDepositLimits() {
        let tempLimits = [];

        this.responsibleGamingProvider.getDepositLimit().subscribe({
            next: (response: any) => {
                if (response && !!response.result) {
                    tempLimits = response.payload;

                    for (const index in tempLimits) {
                        if (tempLimits[index].limitSource != "GLOBAL") {
                            this.depositLimits.push(tempLimits[index]);
                        }
                    }

                    this.updateDepositLimits(this.depositLimits);
                }
            },
            error: (error) => {
                this.error = error;
            }
        });
    }

    updateDepositLimits(depositLimits: any[]) {
        this.deposit_daily_limit.setValue("");
        this.deposit_weekly_limit.setValue("");
        this.deposit_monthly_limit.setValue("");

        this.deposit_daily_limit.markAsPristine();
        this.deposit_weekly_limit.markAsPristine();
        this.deposit_monthly_limit.markAsPristine();

        for (const i in depositLimits) {
            if (depositLimits[i] != null) {
                if (depositLimits[i].currentLimitTermType == "DAILY") {
                    this.dailyDepositLimits = depositLimits[i];
                }

                if (depositLimits[i].currentLimitTermType == "WEEKLY") {
                    this.weeklyDepositLimits = depositLimits[i];
                }

                if (depositLimits[i].currentLimitTermType == "MONTHLY") {
                    this.monthlyDepositLimits = depositLimits[i];
                }
            }
        }
    }

    deleteLimit(term: string) {
        this.successMessageService.clearMessage();
        this.error = null;

        const requestObj: any = {};
        requestObj.termType = term.toUpperCase();

        this.responsibleGamingProvider.deleteDepositLimit(requestObj).subscribe({
            next: (response: any) => {
                if (response && !!response.result) {
                    this.getDepositLimits();
                }
            },
            error: (error) => {
                this.error = error;
            }
        });
    }

    setLimits() {
        this.successMessageService.clearMessage();
        this.error = null;

        this.responsibleGamingProvider.setDepositLimit(this.depositLimitsForm.value).subscribe({
            next: (response: any) => {
                if (response && !!response.result) {
                    this.getDepositLimits();

                    this.successMessageService.setMessage('Your deposit limits were set.');
                    this.content.scrollToTop(10);
                }
            },
            error: (error) => {
                switch (error.getErrorCode().toUpperCase()) {
                    case 'INVALID_TERM_TYPE':
                        error.setErrorMessage('Limit cannot be 0!');
                        break;
                    default:
                        error.setErrorMessage('');
                        break;
                }
                this.error = error;
                this.content.scrollToTop(10);
            }
        });
    }

    onLimitChanged(term) {
        console.log('limitChanged');
        if(term.toUpperCase() == "DAILY") {
            this.dailyLimitChanged();
        } else if(term.toUpperCase() == "WEEKLY") {
            this.weeklyLimitChanged();
        } else if(term.toUpperCase() == "MONTHLY") {
            this.monthlyLimitChanged();
        }
    }

    dailyLimitChanged() {
        if (this.globalDailyLimit != null) {
            this.dailyLimitError = parseInt(this.deposit_daily_limit.value) > parseInt(this.globalDailyLimit);
        }
    }

    weeklyLimitChanged() {
        if (this.globalWeeklyLimit != null) {
            this.weeklyLimitError = parseInt(this.deposit_weekly_limit.value) > parseInt(this.globalWeeklyLimit);
        }
    }

    monthlyLimitChanged() {
        if (this.globalMonthlyLimit != null) {
            this.monthlyLimitError = parseInt(this.deposit_monthly_limit.value) > parseInt(this.globalMonthlyLimit);
        }
    }

    atLeastOneFieldValidator(group: FormGroup): { [key: string]: any } {
        let isAtLeastOne = false;
        if (group && group.controls) {
            for (const control in group.controls) {
                if (Object.prototype.hasOwnProperty.call(group.controls, control) && group.controls[control].valid && group.controls[control].value) {
                    isAtLeastOne = true;
                    break;
                }
            }
        }
        return isAtLeastOne ? null : { 'required': true };
    }

    enterPressed() {
        if (!this.depositLimitsForm.invalid) {
            this.setLimits();
        }
    }

    onClose = () =>{
        this.limitsService.onCloseCommon(this.previousPage == "SignupResult");
    }
}
